import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
	DDSAttendance,
	DDSAttendanceQr,
	DDSAttendanceStatus,
	DDSCheckInResult,
	DDSMood,
} from '@/types'
import {
	checkIn,
	closeAttendance,
	getAttendance,
	getAttendanceQr,
} from '../services/dds-attendance'

interface CheckInVariables {
	token: string
	mood: DDSMood
}

const attendanceKey = (ddsId: string) => ['dds', ddsId, 'attendance']
const attendanceQrKey = (ddsId: string) => ['dds', ddsId, 'attendance', 'qr']

// Renova o QR um pouco antes de expirar, para a tela nunca exibir código vencido
const QR_RENEW_BEFORE_EXPIRY_MS = 10 * 1000
const QR_MIN_REFETCH_MS = 5 * 1000
const ATTENDANCE_POLL_MS = 5 * 1000

export function useAttendanceQr(ddsId: string, enabled: boolean) {
	return useQuery<DDSAttendanceQr, Error>({
		queryKey: attendanceQrKey(ddsId),
		queryFn: () => getAttendanceQr(ddsId),
		enabled,
		staleTime: 0,
		gcTime: 0,
		retry: false,
		refetchOnWindowFocus: true,
		refetchInterval: (query) => {
			const expiresAt = query.state.data?.expiresAt
			if (!expiresAt) return false
			const msUntilRenew =
				new Date(expiresAt).getTime() - Date.now() - QR_RENEW_BEFORE_EXPIRY_MS
			return Math.max(msUntilRenew, QR_MIN_REFETCH_MS)
		},
	})
}

export function useAttendance(ddsId: string, poll: boolean) {
	return useQuery<DDSAttendance, Error>({
		queryKey: attendanceKey(ddsId),
		queryFn: () => getAttendance(ddsId),
		enabled: !!ddsId,
		staleTime: 0,
		refetchInterval: poll ? ATTENDANCE_POLL_MS : false,
	})
}

function useInvalidateAttendance(ddsId: string) {
	const queryClient = useQueryClient()
	return () => {
		queryClient.invalidateQueries({ queryKey: ['dds'] })
		queryClient.removeQueries({ queryKey: attendanceQrKey(ddsId) })
	}
}

export function useCloseAttendance(ddsId: string) {
	const invalidate = useInvalidateAttendance(ddsId)
	return useMutation<DDSAttendanceStatus, Error, void>({
		mutationFn: () => closeAttendance(ddsId),
		onSuccess: invalidate,
	})
}

export function useCheckIn() {
	return useMutation<DDSCheckInResult, Error, CheckInVariables>({
		mutationFn: ({ token, mood }) => checkIn(token, mood),
	})
}
